import type { EndpointBuilder } from '@reduxjs/toolkit/query'
// @ts-ignore - nanoid is ESM but works fine at runtime
import { nanoid } from 'nanoid'

import {
  GET_INVITEES,
  GET_MEETING,
  GET_MEETINGS,
  INVITEE_TAG_TYPE,
  MEETING_BUFFER_TAG_TYPE,
  MEETING_TAG_TYPE,
  activeValue,
  buildMeetingListTag,
  buildMeetingTag,
  getBaseUrl,
  hostValue
} from './constants'
import {
  BuffersResult,
  GetBuffersArgs,
  GetInviteesArgs,
  GetMeetingArgs,
  GetMeetingsArgs,
  GetScopeInfoArgs,
  GetScopeInfoResult,
  GetTimelineDataArgs,
  InviteeListResult,
  LookupUuidsArgs,
  Meeting,
  MeetingDay,
  MeetingListResult,
  MeetingResult,
  TimelineDataResult,
  TimelineItem
} from './types'

export const buildQueries = (build: EndpointBuilder<any, any, any>) => ({
  [GET_MEETING]: build.query({
    query: ({ eventId, scopeType, scopeId, selectedView, shareId }: GetMeetingArgs) => ({
      url: `${getBaseUrl(selectedView)}/${eventId || shareId}`,
      params: {
        scopeType,
        scopeId,
        ...(!eventId && Boolean(shareId) ? { is_share: true } : {})
      }
    }),
    // Normalize query args to prevent duplicate cache entries when scope params are undefined
    // This ensures that calls with undefined scopeType/scopeId are treated the same as calls without them
    serializeQueryArgs: (
      { queryArgs }: { queryArgs: GetMeetingArgs }
    ) => {
      const normalized: GetMeetingArgs = {
        eventId: queryArgs.eventId,
        shareId: queryArgs.shareId,
        selectedView: queryArgs.selectedView || 'scheduled',
      }
      // Only include scope params if they're defined (not undefined/null)
      if (queryArgs.scopeType) {
        normalized.scopeType = queryArgs.scopeType
      }
      if (queryArgs.scopeId) {
        normalized.scopeId = queryArgs.scopeId
      }
      return normalized
    },
    providesTags: (result: MeetingResult | undefined, error: unknown, args: GetMeetingArgs) => {
      const tags = [buildMeetingListTag()]
      if (result?.uuid) {
        tags.push(buildMeetingTag(result.uuid))
      } else if (args.eventId || args.shareId) {
        // If we have an ID but no result yet, still tag the individual meeting
        tags.push(buildMeetingTag(args.eventId || args.shareId))
      }
      return tags
    },
    // Suppress error alert: meeting detail uses REST then GraphQL fallback on 404; we clear rail after, no toast needed
    extraOptions: { handleErrors: false }
  }),
  [GET_MEETINGS]: build.query({
    query: ({
      page = 1,
      period,
      scopeId,
      scopeType,
      startDate,
      endDate,
      hostIds = [hostValue],
      statusIds = [activeValue],
      teamIds = [],
      userIds = [],
      eventTypeIds,
      utmCampaign,
      utmSource,
      utmMedium,
      utmContent,
      utmTerm,
      inviteeEmails,
      selectedView
    }: GetMeetingsArgs = {}) => ({
      url: getBaseUrl(selectedView),
      params: {
        page,
        period,
        scopeId,
        scopeType,
        hostIds,
        statusIds,
        teamIds,
        userIds,
        eventTypeIds,
        utmCampaign,
        utmSource,
        utmMedium,
        utmContent,
        utmTerm,
        inviteeEmails,
        ...(startDate && endDate ? { startDate, endDate } : {})
      }
    }),
    providesTags: (result: MeetingListResult | undefined, _error: unknown) => {
      const tags = [buildMeetingListTag()]
      if (result?.results && Array.isArray(result.results)) {
        // result.results contains day objects, each with an events array
        // Iterate over days and extract meetings from day.events[]
        result.results.forEach((day: MeetingDay) => {
          if (day?.events && Array.isArray(day.events)) {
            day.events.forEach((meeting: Meeting) => {
              if (meeting?.uuid) {
                tags.push(buildMeetingTag(meeting.uuid))
              }
            })
          }
        })
      }
      return tags
    },
    extraOptions: {
      handleErrors: false
    }
  }),
  [GET_INVITEES]: build.query({
    query: ({ eventId, page }: GetInviteesArgs) => ({
      url: `/api/dashboard/events/${eventId}/invitees`,
      params: {
        page
      }
    }),
    serializeQueryArgs: ({ queryArgs: { eventId } }: { queryArgs: GetInviteesArgs }) => ({ eventId }),
    merge: (currentCache: InviteeListResult, newItems: InviteeListResult) => {
      currentCache.pagination = newItems.pagination
      if (newItems.pagination.currentPage > 1) {
        currentCache.results.push(...newItems.results)
      } else {
        currentCache.results = newItems.results
      }
    },
    // Compare by page, not object identity — a new `{ eventId, page }` each render
    // would force-refetch forever (e.g. after I18n locale refresh re-renders).
    forceRefetch: ({ currentArg, previousArg }: {
      currentArg: GetInviteesArgs
      previousArg: GetInviteesArgs | undefined
    }) => currentArg?.page !== previousArg?.page,
    providesTags: [INVITEE_TAG_TYPE]
  }),
  lookupUuids: build.query({
    query: ({ filters }: LookupUuidsArgs) => ({
      url: '/api/users/me/filters/lookup_uuids',
      method: 'POST',
      data: { filters }
    })
  }),
  getPollResults: build.query({
    query: (eventTypeId: string | number) => ({
      url: `/api/scheduled_events/polls/${eventTypeId}`
    })
  }),
  getScopeInfo: build.query<GetScopeInfoResult, GetScopeInfoArgs>({
    query: ({ scopeType, scopeId }: GetScopeInfoArgs) => ({
      url: '/api/event_types/scope_dropdown/current_scope',
      params: {
        current_scope: scopeType,
        owner_id: scopeId
      }
    })
  }),
  getBuffers: build.query({
    query: ({ uuids }: GetBuffersArgs) => ({
      url: '/api/dashboard/events/fetch_buffers',
      method: 'POST',
      data: { uuids }
    }),
    providesTags: (result: BuffersResult | undefined) => (result?.events
      ? [
        ...result.events.flatMap((event) => [
          { type: MEETING_BUFFER_TAG_TYPE, id: event.uuid },
          ...event.buffers.map((buffer) => ({
            type: MEETING_BUFFER_TAG_TYPE,
            id: `${event.uuid}-${buffer.id}`
          }))
        ]),
        MEETING_TAG_TYPE
      ]
      : [MEETING_TAG_TYPE])
  }),
  getTimelineData: build.query({
    query: ({ eventId, workflowsExtractionEnabled }: GetTimelineDataArgs) => ({
      url: workflowsExtractionEnabled ? `/api/apps/workflows/timeline?event_id=${eventId}` : `/api/dashboard/events/${eventId}/timeline`
    }),
    // Add keys to timeline_widget items to match the structure expected by Timeline component
    // This matches the normalizeKeys behavior from the old Redux action
    // Also convert camelCase properties back to snake_case for Entry component compatibility
    transformResponse: (response: TimelineDataResult): TimelineItem[] => {
      if (!Array.isArray(response)) {
        return response as TimelineItem[]
      }
      return response.map((item: TimelineItem | Record<string, any>): TimelineItem => {
        // root-api transforms snake_case to camelCase, but Entry component expects snake_case
        // Convert camelCase properties back to snake_case for component compatibility
        const convertedItem: TimelineItem = {
          key: nanoid(5),
          ...item
        }

        // Map camelCase to snake_case for properties used by Entry component
        if (item.actionConfigurationType && !item.action_configuration_type) {
          convertedItem.action_configuration_type = item.actionConfigurationType
        }
        if (item.workflowName && !item.workflow_name) {
          convertedItem.workflow_name = item.workflowName
        }
        if (item.isGroup !== undefined && item.is_group === undefined) {
          convertedItem.is_group = item.isGroup
        }
        if (item.actionId !== undefined && item.action_id === undefined) {
          convertedItem.action_id = item.actionId
        }
        if (item.targetId !== undefined && item.target_id === undefined) {
          convertedItem.target_id = item.targetId
        }
        // Properties needed for timeline_widget entry components
        if (item.inviteeName && !item.invitee_name) {
          convertedItem.invitee_name = item.inviteeName
        }
        if (item.eventName && !item.event_name) {
          convertedItem.event_name = item.eventName
        }
        if (item.cancelerName !== undefined && item.canceler_name === undefined) {
          convertedItem.canceler_name = item.cancelerName
        }

        return convertedItem
      })
    }
  })
})
